import { useState, useMemo, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { isClientTier, roleAccentColor, roleLabel } from "../lib/roles";
import { KeyRound, AtSign, Building2, ArrowRight, Clock, X, ShieldAlert } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "sonner";
import { listRecentWorkspaces, forgetRecentWorkspace, clearRecentWorkspaces } from "../lib/recentWorkspaces";

/**
 * iter279 — Format a retry-after countdown for the SEC-004 lockout banner.
 * Under a minute renders as "in X seconds", ≥ 1 minute renders as "in N min NNs"
 * so the officer sees the countdown tick even when it drops below the minute.
 */
const formatRetry = (seconds) => {
  if (seconds <= 0) return "any moment now";
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const mins = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (rem === 0) return `${mins} minute${mins === 1 ? "" : "s"}`;
  return `${mins} min ${rem.toString().padStart(2, "0")}s`;
};

export default function Login() {
  const { login, loginSelectAgency, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // iter279 — SEC-004 lockout state. When the API returns 429 we capture
  // the Retry-After timestamp and tick a countdown so the form disables
  // + surfaces a friendly "try again in X" banner. Auto-clears at zero.
  const [lockoutUntil, setLockoutUntil] = useState(null);  // epoch ms
  const [tick, setTick] = useState(0);  // re-render pulse for the countdown
  const secondsRemaining = lockoutUntil
    ? Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000))
    : 0;
  const isLockedOut = secondsRemaining > 0;

  useEffect(() => {
    if (!lockoutUntil) return undefined;
    const iv = setInterval(() => {
      const remaining = lockoutUntil - Date.now();
      if (remaining <= 0) {
        setLockoutUntil(null);
      } else {
        // Trigger a re-render so `secondsRemaining` recomputes.
        setTick((v) => (v + 1) % 1000);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [lockoutUntil]);
  // Multi-agency picker state — populated when /auth/login returns a
  // {multi_agency: true, ticket, agencies: [...]} payload. Rendering the
  // picker inline instead of on a new route keeps the whole flow on the
  // single Login page (state clears the moment they pick an agency).
  const [picker, setPicker] = useState(null);
  // Pinned agency for the NEXT submit — set when the user clicks a "Recent"
  // pill. Sent as `agency_id` in the login payload so the backend skips
  // the picker altogether. Cleared after submit or manual reset.
  const [pinnedAgencyId, setPinnedAgencyId] = useState(null);
  const [pinnedAgencyName, setPinnedAgencyName] = useState(null);
  // Recent-workspaces list — MRU pill row above the form. `recentTick`
  // is bumped when the list changes (delete/add) so this memo re-runs.
  // We intentionally KEEP `recentTick` in deps as a re-render trigger
  // even though ESLint marks it "unnecessary" (the fn reads localStorage,
  // not `recentTick`) — removing it defeats the whole purpose.
  const [recentTick, setRecentTick] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recents = useMemo(() => listRecentWorkspaces(), [recentTick]);

  if (loading) return null;
  if (user) {
    const dest = isClientTier(user) ? "/portal" : "/";
    return <Navigate to={dest} replace />;
  }

  const routeForUser = (u) => (
    isClientTier(u) ? "/portal" : "/"
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isLockedOut) return;  // safety net — button is already disabled
    setSubmitting(true);
    try {
      const result = await login(email, password, pinnedAgencyId);
      // Multi-agency identity — surface the picker; user picks a tenant
      // and we then call loginSelectAgency() to complete the session.
      if (result && result.multiAgency) {
        setPicker({ ticket: result.ticket, agencies: result.agencies });
        return;
      }
      toast.success(pinnedAgencyName ? `Signed into ${pinnedAgencyName}` : "Access granted");
      nav(routeForUser(result));
    } catch (err) {
      // iter279 — SEC-004 lockout: the API returns 429 + Retry-After.
      // Parse the header (or the default 15-min lockout as fallback) and
      // flip into lockout mode so the banner appears + submit disables.
      if (err?.response?.status === 429) {
        const hdr = err.response.headers?.["retry-after"] ?? err.response.headers?.["Retry-After"];
        const parsed = parseInt(hdr, 10);
        const seconds = Number.isFinite(parsed) && parsed > 0 ? parsed : 900;
        setLockoutUntil(Date.now() + seconds * 1000);
        return;  // banner supersedes the generic toast
      }
      const detail = err?.response?.data?.detail || "Authentication failed";
      toast.error(typeof detail === "string" ? detail : "Authentication failed");
      // If we pre-pinned an agency that no longer has a live membership,
      // clear the pin so the user can retry cleanly with the picker path.
      if (err?.response?.status === 404 && pinnedAgencyId) {
        setPinnedAgencyId(null); setPinnedAgencyName(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pickRecent = (entry) => {
    // One-click prefill — populate the credentials fields, remember the
    // pinned agency for the next submit, and jump focus to the password
    // input so the user can immediately type + hit Enter.
    setEmail(entry.email);
    setPinnedAgencyId(entry.agency_id);
    setPinnedAgencyName(entry.agency_name || null);
    setPassword("");
    setTimeout(() => {
      const pw = document.getElementById("password");
      if (pw) pw.focus();
    }, 0);
  };

  const removeRecent = (e, entry) => {
    e.stopPropagation();
    forgetRecentWorkspace(entry.email, entry.agency_id);
    setRecentTick((v) => v + 1);
    if (pinnedAgencyId === entry.agency_id) {
      setPinnedAgencyId(null);
      setPinnedAgencyName(null);
    }
  };

  const pickAgency = async (agencyId) => {
    setSubmitting(true);
    try {
      const chosen = picker.agencies.find((a) => a.agency_id === agencyId);
      const u = await loginSelectAgency(picker.ticket, agencyId, chosen?.agency_name);
      toast.success(`Signed into ${chosen?.agency_name || "your agency"}`);
      nav(routeForUser(u));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not open that membership";
      toast.error(typeof detail === "string" ? detail : "Could not open that membership");
      // If the ticket expired we drop the user back to the credentials form.
      if (err?.response?.status === 401) setPicker(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0A0A] text-white">
      <div className="login-hero hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-[#27272A]">
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1555664424-778a1e5e1b48?q=80&w=2000&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Near-solid dark overlay. The image below sits at 12% opacity
            for texture; overlay stays > 96% dark everywhere so headlines
            & labels always win. Faint red accent stays at the same alpha
            as the background to avoid a wash-out at the corner. */}
        <div
          className="absolute inset-0 scanlines"
          style={{
            background:
              "linear-gradient(135deg, rgba(10,10,10,0.98) 0%, rgba(10,10,10,0.97) 60%, rgba(30,10,12,0.97) 100%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3">
            <BrandLogo variant="full" height={54} />
          </div>
        </div>
        <div className="relative">
          <div className="label-mute mb-3">Command Center</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black uppercase tracking-tighter leading-[0.9]">
            Manage every<br />officer,<br />
            <span className="text-[#FF3B30]">every shift.</span>
          </h1>
          <p className="text-zinc-300 mt-6 max-w-md text-sm tracking-wide">
            A tactical operations console for security agencies. Roster, attendance, incidents, and payroll—unified.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-4">
          {[
            { label: "Uptime", value: "99.98%" },
            { label: "Sites", value: "24/7" },
            { label: "Audit", value: "ISO" },
          ].map((s) => (
            <div key={s.label} className="border border-[#27272A] p-3">
              <div className="label-mute">{s.label}</div>
              <div className="font-mono font-bold text-xl mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        {picker ? (
          <div
            className="w-full max-w-md border border-[#27272A] bg-[#121212] p-8"
            data-testid="agency-picker"
          >
            <div className="label-mute">Which agency?</div>
            <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-1">Pick a workspace</h2>
            <p className="text-zinc-400 text-sm mt-2">
              This email is a member of <span className="text-white font-semibold">{picker.agencies.length}</span> agencies.
              Pick the one you&apos;re signing in to — you can switch later from the top-right menu.
            </p>

            <ul className="mt-8 space-y-2">
              {picker.agencies.map((a) => (
                <li key={a.agency_id || a.user_id}>
                  <button
                    onClick={() => pickAgency(a.agency_id)}
                    disabled={submitting}
                    data-testid={`agency-picker-opt-${a.agency_id || "no-tenant"}`}
                    className="w-full flex items-center gap-3 px-4 py-3 border border-[#27272A] hover:border-[#FF3B30] hover:bg-[#1a1a1a] transition-colors group disabled:opacity-40"
                  >
                    <Building2 className="size-4 text-zinc-500 group-hover:text-[#FF3B30]" />
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-display uppercase tracking-tight font-bold truncate">{a.agency_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{(a.role || "").replace(/_/g, " ")}</div>
                    </div>
                    <ArrowRight className="size-4 text-zinc-600 group-hover:text-[#FF3B30]" />
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setPicker(null)}
              data-testid="agency-picker-back"
              className="mt-6 text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
            >
              ← Sign in with a different email
            </button>
          </div>
        ) : (
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md border border-[#27272A] bg-[#121212] p-8"
          data-testid="login-form"
        >
          <div className="label-mute">Authenticate</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-1">Operator Login</h2>
          <p className="text-zinc-400 text-sm mt-2">Restricted access. All sessions are logged.</p>
          <p className="text-xs text-zinc-500 mt-2">
            New here?{" "}
            <Link to="/signup" className="text-zinc-300 hover:text-[#FF3B30] underline" data-testid="login-to-signup-link">
              Create an agency
            </Link>
          </p>

          {/* Recent workspaces — MRU pill row. One click prefills email +
              pins the target agency so the next submit skips the picker. */}
          {recents.length > 0 && (
            <div className="mt-6 border border-[#27272A] bg-[#0A0A0A] p-3" data-testid="recent-workspaces">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 label-mute">
                  <Clock className="size-3" /> Recent
                </div>
                <button
                  type="button"
                  data-testid="recent-workspaces-clear"
                  onClick={() => { clearRecentWorkspaces(); setRecentTick((v) => v + 1); }}
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {recents.map((r) => {
                  const active = pinnedAgencyId === r.agency_id && email === r.email;
                  return (
                    <div
                      key={`${r.email}-${r.agency_id}`}
                      className={`group inline-flex items-center gap-2 px-2.5 py-1.5 border transition-colors cursor-pointer text-xs ${
                        active
                          ? "border-[#FF3B30] bg-[#FF3B30]/10 text-white"
                          : "border-[#27272A] hover:border-[#FF3B30] text-zinc-300"
                      }`}
                      onClick={() => pickRecent(r)}
                      data-testid={`recent-workspace-${r.agency_id}`}
                    >
                      <span
                        aria-hidden
                        className="size-2 rounded-full shrink-0"
                        style={{ backgroundColor: roleAccentColor(r.role) }}
                        title={roleLabel(r.role)}
                      />
                      <Building2 className="size-3 shrink-0 text-zinc-500 group-hover:text-[#FF3B30]" />
                      <div className="min-w-0">
                        <div className="font-semibold truncate max-w-[140px]">
                          {r.agency_name || r.agency_id.slice(0, 8)}
                        </div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono truncate max-w-[140px]">
                          {r.email}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => removeRecent(e, r)}
                        title="Forget this workspace"
                        data-testid={`recent-workspace-forget-${r.agency_id}`}
                        className="opacity-40 hover:opacity-100 hover:text-[#FF3B30]"
                      >
                        <X className="size-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
              {pinnedAgencyName && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-[#FF9F0A]" data-testid="recent-workspace-pinned-hint">
                  Signing into {pinnedAgencyName}
                  <button
                    type="button"
                    onClick={() => { setPinnedAgencyId(null); setPinnedAgencyName(null); }}
                    data-testid="recent-workspace-unpin"
                    className="ml-2 text-zinc-500 hover:text-[#FF3B30] normal-case tracking-normal"
                  >
                    (unpin)
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 space-y-5">
            <div>
              <label className="label-mute block mb-2" htmlFor="email">Email or Username</label>
              <div className="flex items-center border border-[#27272A] focus-within:border-[#FF3B30] transition-colors">
                <AtSign className="size-4 text-zinc-500 ml-3" />
                <input
                  data-testid="login-email-input"
                  id="email"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="you@example.com or username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-3 py-3 outline-none text-sm"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-mute" htmlFor="password">Password</label>
                <a
                  href="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
                >
                  Forgot?
                </a>
              </div>
              <div className="flex items-center border border-[#27272A] focus-within:border-[#FF3B30] transition-colors">
                <KeyRound className="size-4 text-zinc-500 ml-3" />
                <input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent px-3 py-3 outline-none text-sm"
                />
              </div>
            </div>
          </div>

          {isLockedOut && (
            <div
              data-testid="login-lockout-banner"
              className="mt-6 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-4 py-3 flex items-start gap-3"
            >
              <ShieldAlert className="size-4 text-[#FF3B30] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-[#FF3B30] font-mono">
                  Too many attempts
                </div>
                <div className="text-sm text-zinc-200 mt-1">
                  This account or IP is temporarily locked. Try again in{" "}
                  <span
                    data-testid="login-lockout-countdown"
                    className="font-mono text-white"
                  >
                    {formatRetry(secondsRemaining)}
                  </span>
                  .
                </div>
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Not you? An admin can review the security log for the failed
                  attempts. This lockout is a defense against brute-force login
                  attempts (SEC-004).
                </div>
              </div>
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={submitting || isLockedOut}
            className="w-full mt-8 bg-[#FF3B30] hover:bg-[#FF453A] text-white font-display uppercase tracking-widest text-sm py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {isLockedOut
              ? `Locked · retry in ${formatRetry(secondsRemaining)}`
              : (submitting ? "Authenticating…" : "Initiate Session")}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
